'use client';

import { useEffect, useRef, useState } from 'react';

import { ReserveNowCard } from '@checkout/Components/ReserveNowCard/ReserveNowCard';
import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import { DEFAULT_LOCALE, DE_LOCALE, FR_LOCALE } from '@core/Constants/Locale.constants';
import { canShowReserve } from '@core/Utils/Vehicle/CanShowReserve';
import { isHeycarConnectDealer } from '@core/Utils/Vehicle/IsHeycarConnect';
import { isInstaMotion } from '@core/Utils/Vehicle/IsInstaMotion';
import { isVtiVehicle } from '@core/Utils/Vehicle/IsVtiVehicle';
import Button from '@gds/Button/Button';
import Logo from '@gds/Logo/Logo';
import { Typography } from '@gds/Typography/Typography';
import { experimentsAtom } from '@growthBookExperimentation/Atoms/Experiments.atom';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { useCallDealer } from '@lead-forms/Hooks/UseCallDealer';
import { generateLeadTrackingData } from '@lead-forms/Tracking/LeadForms.tracking';
import { useModal } from '@modal/Hooks/UseModal';
import { generateCheckoutStartTrackingData } from '@tracking/LeadCTA.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';
import { RacLogo } from '@whitelabel/Components/RacLogo';
import cn from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';

import { leadFormIdAtom } from 'Atoms/App/Pdp/LeadFormId.atom';

import { CallDealerCTA } from 'Components/LeadForms/CallDealer/CallDealerCTA';
import useLeadFormHeaderTextExperiment from 'Components/LeadForms/Hooks/useLeadFormHeaderTextExperiment';
import { LeadForm } from 'Components/LeadForms/LeadForm/LeadForm';
import { isInstantCallbackEnabled } from 'Components/LeadForms/MessageDealer/IsInstantCallbackEnabled/IsInstantCallbackEnabled';
import { generateReservationTrackingData } from 'Components/LeadForms/ReservationCheckoutCta/ReservationCheckoutCta.tracking';

import leadFormStyles from '../../LeadForms/LeadForm/LeadForm.module.css';

import { getLeadCtasForDisplay, getNumOfCTAs } from './LeadCTAHelpers';
import { LeadCTAsProps, LeadType } from './LeadCTAs.entity';
import { LeadCtaConfig, leadCtasConfig } from './LeadCtaConfig';

import styles from './LeadCTAs.module.css';

export const LeadCTAs = ({
  vehicle,
  strings: { pdpStrings: messages, leadFormMessages, reserveNowFaqContent },
  isEcom,
  locale,
  hasReservedOrInactiveStatus,
  isSticky: isStickyCta,
  hasStartedAutoGenerateNo,
}: LeadCTAsProps) => {
  const leadCTAsRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const flags = useAtomValue(flagsAtom);

  useEffect(() => {
    setIsReady(true);
    const headerHeight = '-75'; // Fixed header height for mobile
    const options = {
      root: null,
      rootMargin: `${headerHeight}px 0px 0px 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver(entries => {
      const query = window.matchMedia(`(max-width: 767px)`);
      const isIntersecting = entries[0].isIntersecting;
      setIsSticky(!isIntersecting && query.matches);
    }, options);

    const targetElement = flags.stickyCta2PdpMobileExperiment
      ? document.getElementById('sticky-info')
      : leadCTAsRef.current;

    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => {
      if (targetElement) {
        observer.unobserve(targetElement);
      }
    };
  }, []);
  const isFr = locale === FR_LOCALE;
  const isUk = locale === DEFAULT_LOCALE;
  const isDe = locale === DE_LOCALE;

  const tenant = useAtomValue(tenantAtom);
  const experiments = useAtomValue(experimentsAtom);
  const listingId = useAtomValue(currentVehicleIdAtom);
  const isVsp = vehicle?.spec?.d2cModel === 'vsp';
  const isInvestorStockLayout = isUk && tenant !== 'rac' && vehicle?.spec?.d2c && isEcom;
  const setLeadFormId = useSetAtom(leadFormIdAtom);
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const { openModal } = useModal();
  const formState = useAtomValue(formStateAtom);
  const { shouldAutoGenNumber } = useCallDealer();
  const hasReserveNowFeature = canShowReserve(locale, vehicle, flags.reserveNowNewFeature);
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);

  const hasHeycarConnect = flags.heycarConnect && isHeycarConnectDealer(vehicle);

  const orderedLeadCtasConfig = orderLeadCtas(leadCtasConfig, !isUk);
  const ctasToDisplay = getLeadCtasForDisplay(orderedLeadCtasConfig, flags, isEcom, vehicle);
  const isInActiveVehicle = vehicle?.details.status === 'inactive';
  const isInstamotionVehicle = isInstaMotion(vehicle?.details?.dataSource);
  const leadyTypes = ctasToDisplay.map(([id]) => id as LeadType);

  const { leadTypeToMessageMap } = useLeadFormHeaderTextExperiment(leadyTypes, vehicle, {
    ...leadFormMessages,
    ...messages,
  });

  const onLeadModal = (leadId: LeadType) => {
    const {
      leadStart: { event, context },
    } = generateLeadTrackingData({
      searchParams,
      userId,
      formState,
      leadFormId: leadId,
      leadId: '',
      listingId,
      vehicle,
      ctaLocation: 'pdp',
      experiments,
      leadAction: '',
      isInstantCallbackEnabled: isInstantCallbackEnabled({
        flags,
        locale,
        dealerOpeningTimes: vehicle.dealer.openingTimes ?? [],
      }),
    });
    trackCustomEvent({ event, context });

    if (flags.refactoredLeadForms && leadId !== 'call-dealer') {
      toggleLeadForm(leadId);
    } else {
      setLeadFormId(leadId);
      // @ts-expect-error - onClick is optional
      if (leadCtasConfig[leadId]?.onClick) {
        // @ts-expect-error - onClick is optional
        leadCtasConfig[leadId]?.onClick?.({ flags, isEcom, vehicle });
        return;
      }
      openModal(<LeadForm strings={{ ...leadFormMessages, ...messages }} locale={locale} />, {
        header: leadTypeToMessageMap && leadTypeToMessageMap[leadId],
        modalClassName: leadFormStyles.modalContainer,
        modalBodyClassName: leadFormStyles.modalBodyClassName,
      });
    }
  };

  const handleClickCheckout = () => {
    const {
      checkoutStart: { event, context },
    } = generateCheckoutStartTrackingData({
      searchParams,
      userId,
      formState,
      listingId,
      vehicle,
      ctaLocation: 'pdp',
      experiments,
    });
    trackCustomEvent({ event, context });
  };

  const handleClickReserveNow = () => {
    const {
      reserveStart: { event, context },
    } = generateReservationTrackingData({
      searchParams,
      userId,
      formState,
      listingId,
      vehicle,
    });
    trackCustomEvent({ event, context });
  };

  const shouldShowStickyCta = isSticky || (isSticky && isInvestorStockLayout);

  const numOfCtas = getNumOfCTAs(
    ctasToDisplay,
    hasReservedOrInactiveStatus,
    isEcom,
    hasReserveNowFeature,
  );

  const deEcomUrl = isDe
    ? `/${locale}/checkout/${vehicle?.heycarId}/${
        isVtiVehicle(vehicle?.details?.dataSource) ? 'vti-cash' : 'dealer'
      }/about-you`
    : '';

  return (
    <div
      ref={leadCTAsRef}
      className={`${shouldShowStickyCta ? styles.minHeightZero : styles.leadButtonsOuter} ${
        styles[`btnNum${numOfCtas}`]
      }`}
    >
      <div
        className={cn(styles.leadButtonsWrapper, {
          [styles.stickyContent]: shouldShowStickyCta,
          [styles.stickyCta]: isStickyCta,
          [styles.vspAndNoneVspMobileCTAWrapper]: isVsp || isEcom,
          [styles.hasReserveNow]: hasReserveNowFeature,
          [styles.vspDesktopLayout]: isInvestorStockLayout,
        })}
      >
        {hasReservedOrInactiveStatus ? (
          <Button disabled>
            {isInActiveVehicle ? messages.inActiveVehicleLabel : messages.reservedVehicleLabel}
          </Button>
        ) : (
          <>
            {isEcom && (
              <>
                {tenant === 'rac' && (
                  <div className={styles.teamedWith}>
                    <RacLogo style={{ fontSize: 100 }} />
                    <Typography Component="span">{messages.buyOnlineWith}</Typography>
                    <Logo usage="light_bg" fontSize={80} />
                  </div>
                )}
                {isFr && flags?.frReservation && (
                  <Link
                    legacyBehavior
                    href={`/${locale}/checkout/${vehicle?.heycarId}/buy/about-you`}
                    className={`${styles.buyOnlineCTA}`}
                  >
                    <Button
                      data-test-id="buy-online-cta"
                      className={`${shouldShowStickyCta ? styles.priorityCTA : ''}`}
                      onClick={handleClickCheckout}
                      disabled={!isReady}
                    >
                      {messages.buyOnline}
                    </Button>
                  </Link>
                )}
                {isDe && (
                  <Link legacyBehavior href={deEcomUrl} className={`${styles.buyOnlineCTA}`}>
                    <Button
                      data-test-id="buy-online-cta"
                      className={`${shouldShowStickyCta ? styles.priorityCTA : ''}`}
                      onClick={handleClickCheckout}
                      disabled={!isReady}
                    >
                      {isInstamotionVehicle ? messages.buyOnlineInstamotion : messages.buyOnline}
                    </Button>
                  </Link>
                )}

                {isUk && (
                  <Link
                    legacyBehavior
                    href={`/${locale}/checkout/${vehicle?.heycarId}/about-you`}
                    className={styles.buyOnlineCTA}
                  >
                    <Button
                      data-test-id="buy-online-cta"
                      className={shouldShowStickyCta ? styles.priorityCTA : ''}
                      onClick={handleClickCheckout}
                      disabled={!isReady}
                    >
                      {messages.buyOnline}
                    </Button>
                  </Link>
                )}
              </>
            )}
            {hasHeycarConnect && (
              <Link
                legacyBehavior
                href={`/${locale}/checkout/${vehicle?.heycarId}/connect/about-you`}
                className={`${styles.buyOnlineCTA}`}
              >
                <Button
                  data-test-id="heycar-connect-cta"
                  className={`${shouldShowStickyCta ? styles.priorityCTA : ''}`}
                  onClick={handleClickCheckout}
                  disabled={!isReady}
                >
                  {messages.connectDealer}
                </Button>
              </Link>
            )}
            {!isUk && !flags.callbackCtaPublicHoliday && (
              <CallDealerCTA
                strings={{ ...messages, ...leadFormMessages }}
                locale={locale}
                isSticky={shouldShowStickyCta}
                isEcom={isEcom}
                disabled={!isReady}
                shouldAutoGenNumber={shouldAutoGenNumber}
                hasStartedAutoGenerateNo={hasStartedAutoGenerateNo}
              />
            )}
            {ctasToDisplay.map(([id, data]) => {
              const isEcomAndSticky = isEcom && shouldShowStickyCta;
              const isNotUkAndSticky = !isUk && shouldShowStickyCta;
              const hasReserveNowAndStickyAndCallback =
                hasReserveNowFeature && shouldShowStickyCta && id === 'request-callback';
              const hasButtonText =
                data.showButtonText &&
                data.showButtonText({ isSticky: shouldShowStickyCta, isEcom, isVsp, isUk });
              return (
                <Button
                  leftIcon={
                    isEcomAndSticky || isNotUkAndSticky || hasReserveNowAndStickyAndCallback
                      ? data.buttonIcon
                      : null
                  }
                  data-test-id={isReady ? data.labelId() : ''}
                  key={id}
                  variant={data.variant({ isEcom, flags, vehicle, isUk })}
                  onClick={() => onLeadModal(id as LeadType)}
                  disabled={!isReady}
                  className={`${
                    id === 'message-dealer' && !isEcom && isUk && shouldShowStickyCta
                      ? styles.priorityCTA
                      : ''
                  }`}
                >
                  {hasReserveNowFeature &&
                  (id === 'message-dealer' || id === 'request-callback') &&
                  shouldShowStickyCta ? (
                    <>
                      <span className={styles.reserveNowCtaDesktop}>
                        {messages[data.labelId(flags)]}
                      </span>
                      <span className={styles.reserveNowCtaMobile}>
                        {id === 'message-dealer' ? messages.message : ''}
                      </span>
                    </>
                  ) : !isInvestorStockLayout && hasButtonText ? (
                    messages[data.labelId(flags)]
                  ) : isInvestorStockLayout && hasButtonText && id !== 'request-callback' ? (
                    messages.message
                  ) : isInvestorStockLayout && hasButtonText && id === 'request-callback' ? (
                    messages.call
                  ) : (
                    ''
                  )}
                </Button>
              );
            })}

            {isUk && (
              <CallDealerCTA
                strings={{ ...messages, ...leadFormMessages }}
                locale={locale}
                isSticky={shouldShowStickyCta}
                isEcom={isEcom}
                hasReserveNow={hasReserveNowFeature}
                disabled={!isReady}
                shouldAutoGenNumber={shouldAutoGenNumber}
                hasStartedAutoGenerateNo={hasStartedAutoGenerateNo}
              />
            )}

            {hasReserveNowFeature && (
              <Link legacyBehavior href={`/uk/checkout/${vehicle?.heycarId}/reservation/about-you`}>
                <Button
                  variant="outlined"
                  data-test-id={isReady ? 'reserve-now-cta' : ''}
                  className={`${shouldShowStickyCta ? styles.priorityCTA : ''}`}
                  onClick={handleClickReserveNow}
                  disabled={!isReady}
                >
                  <span>{shouldShowStickyCta ? messages.reserve : messages.reserveNow}</span>
                </Button>
              </Link>
            )}
          </>
        )}
      </div>
      {hasReserveNowFeature && (
        <div className={styles.reserveNowWrapper}>
          <ReserveNowCard strings={leadFormMessages} faqContent={reserveNowFaqContent} />
        </div>
      )}
    </div>
  );
};

const orderLeadCtas = (config: LeadCtaConfig, isNotUk: boolean) => {
  if (isNotUk) {
    const { 'request-callback': reqCallback, 'call-dealer': callDealer, ...rest } = config;
    return {
      'request-callback': reqCallback,
      'call-dealer': callDealer,
      ...rest,
    };
  }
  return config;
};
